import { ProjectFormData } from '../types/project-form';
import { getCurrentUser } from '../services/userService';

/**
 * Validate and format date input to ensure it's a proper date string
 */
export const validateAndFormatDate = (dateInput: any): string => {
  if (!dateInput) {
    return '';
  }
  
  // If it's already a string and looks like a valid date format, return as-is
  if (typeof dateInput === 'string') {
    // Check if it's a decimal number (the problematic case)
    if (/^\d*\.?\d+$/.test(dateInput) && parseFloat(dateInput) < 1) {
      console.error('DEBUG - DECIMAL DATE REJECTED in validateAndFormatDate:', dateInput);
      return '';
    }
    
    // Check if it's an empty string
    if (dateInput.trim() === '') {
      return '';
    }
    
    // Check if it's a valid date string
    const testDate = new Date(dateInput);
    if (!isNaN(testDate.getTime())) {
      // Convert to YYYY-MM-DD format for consistency
      const formatted = testDate.toISOString().split('T')[0];
      return formatted;
    }
  }
  
  // If it's a Date object
  if (dateInput instanceof Date) {
    if (!isNaN(dateInput.getTime())) {
      const formatted = dateInput.toISOString().split('T')[0];
      return formatted;
    }
  }
  
  // If it's a number (timestamp)
  if (typeof dateInput === 'number' && dateInput > 1000000000) { // Reasonable timestamp check
    const date = new Date(dateInput);
    if (!isNaN(date.getTime())) {
      const formatted = date.toISOString().split('T')[0];
      return formatted;
    }
  }
  
  console.error('DEBUG - Unable to format date, returning empty string:', dateInput, typeof dateInput);
  return '';
};

/**
 * Transform fishbone analysis data from frontend nested structure to backend flat arrays
 * New structure: cause1, cause2, why1-why5, isPrioritized for root causes
 */
export const transformFishboneDataForBackend = (formData: ProjectFormData) => {
  const result: any = {};

  // DEBUG: Log the incoming fishbone data
  if (formData.fishBoneAnalysis) {
    // Transform each category from nested object to array of IFishBoneCause objects
    const categories = ['peopleCauses', 'processCauses', 'policyCauses', 'placeCauses', 'provisionCauses'] as const;
    // Use the same field names as the backend expects
    const resultFieldNames = ['peopleCauses', 'processCauses', 'policyCauses', 'placeCauses', 'provisionCauses'] as const;

    categories.forEach((category, index) => {
      const categoryData = formData.fishBoneAnalysis?.[category];
      const resultFieldName = resultFieldNames[index];

      if (categoryData) {
        // DEBUG: Log category data to see structure
        // Check if we have the new causeItems format first
        if (categoryData.causeItems && categoryData.causeItems.length > 0) {
          // Use causeItems data (new format) - transform to cause1, cause2, why1-why5 structure
          
          const filteredCauses = categoryData.causeItems.filter(cause => cause.text.trim() !== '');
          
          result[resultFieldName] = filteredCauses.map((cause, index) => {
              // CRITICAL FIX: Why analysis is stored on the individual cause object, not at category level
              // Only causes with rank 1 should have whyAnalysis property
              const hasWhyAnalysis = cause.whyAnalysis && Object.keys(cause.whyAnalysis).length > 0;
              
              return {
                // Use cause1, cause2, etc. instead of just text
                [`cause${index + 1}`]: cause.text.trim(),
                text: cause.text.trim(), // Keep for backward compatibility
                rank: cause.rank || 0, // Include rank for debugging
                impact: cause.impact || 0, // Include scoring fields
                evidence: cause.evidence || 0,
                feasibility: cause.feasibility || 0,
                total: cause.total || 0,
                // Extract why analysis from the individual cause object
                why1: hasWhyAnalysis ? (cause.whyAnalysis?.why1 || '') : '',
                reason1: hasWhyAnalysis ? (cause.whyAnalysis?.why1 || '') : '', // Note: reason1 = why1 in this structure
                why2: hasWhyAnalysis ? (cause.whyAnalysis?.why2 || '') : '',
                reason2: hasWhyAnalysis ? (cause.whyAnalysis?.why2 || '') : '', // Note: reason2 = why2 in this structure
                why3: hasWhyAnalysis ? (cause.whyAnalysis?.why3 || '') : '',
                reason3: hasWhyAnalysis ? (cause.whyAnalysis?.why3 || '') : '', // Note: reason3 = why3 in this structure
                why4: hasWhyAnalysis ? (cause.whyAnalysis?.why4 || '') : '',
                reason4: hasWhyAnalysis ? (cause.whyAnalysis?.why4 || '') : '', // Note: reason4 = why4 in this structure
                why5: hasWhyAnalysis ? (cause.whyAnalysis?.why5 || '') : '',
                reason5: hasWhyAnalysis ? (cause.whyAnalysis?.why5 || '') : '', // Note: reason5 = why5 in this structure
                isPrioritized: cause.rank === 1, // Mark rank 1 as prioritized
                rootCause: hasWhyAnalysis ? (cause.whyAnalysis?.rootCause || '') : ''
              };
            });
        } else if (categoryData.causes) {
          // Fall back to legacy comma-separated causes format
          const causes = categoryData.causes.split(',').map((cause: string) => cause.trim()).filter(Boolean);
          result[resultFieldName] = causes.map((causeText: string, index: number) => {
            // For legacy format, assume first cause gets why analysis if available
            // This is a fallback - ideally all data should use the new causeItems format
            const isHighestRanked = index === 0; // First cause is considered highest ranked in legacy format
            const includeWhyAnalysis = isHighestRanked && categoryData.whyAnalysis;
            
            return {
              [`cause${index + 1}`]: causeText,
              text: causeText, // Keep for backward compatibility
              rank: index + 1, // Assign rank based on position (1 = highest)
              why1: includeWhyAnalysis ? (categoryData.whyAnalysis?.why1 || '') : '',
              reason1: includeWhyAnalysis ? (categoryData.whyAnalysis?.reason1 || '') : '',
              why2: includeWhyAnalysis ? (categoryData.whyAnalysis?.why2 || '') : '',
              reason2: includeWhyAnalysis ? (categoryData.whyAnalysis?.reason2 || '') : '',
              why3: includeWhyAnalysis ? (categoryData.whyAnalysis?.why3 || '') : '',
              reason3: includeWhyAnalysis ? (categoryData.whyAnalysis?.reason3 || '') : '',
              why4: includeWhyAnalysis ? (categoryData.whyAnalysis?.why4 || '') : '',
              reason4: includeWhyAnalysis ? (categoryData.whyAnalysis?.reason4 || '') : '',
              why5: includeWhyAnalysis ? (categoryData.whyAnalysis?.why5 || '') : '',
              reason5: includeWhyAnalysis ? (categoryData.whyAnalysis?.reason5 || '') : '',
              isPrioritized: isHighestRanked,
              rootCause: includeWhyAnalysis ? (categoryData.whyAnalysis?.rootCause || '') : ''
            };
          });
        } else {
          result[resultFieldName] = [];
        }
      } else {
        result[resultFieldName] = [];
      }
    });

    // Set showFishBoneDiagram flag
    result.showFishBoneDiagram = true;
  } else {
    // Initialize empty arrays if no fishbone data
    result.peopleCauses = [];
    result.processCauses = [];
    result.policyCauses = [];
    result.placeCauses = [];
    result.provisionCauses = [];
  }

  // DEBUG: Log the transformed result
  return result;
};

/**
 * Check and prepare required fields for project submission
 */
export const checkRequiredFields = (formData: ProjectFormData, customFormData?: ProjectFormData): any => {
  // Use provided form data or fall back to component state
  const dataToUse = customFormData || formData;

  // Get the current user's jurisdiction data
  const currentUser = getCurrentUser();

  // Create final project data object
  const finalProjectData: Record<string, any> = {
    // Basic project info
    projectTitle: dataToUse.projectTitle || '',
    programmeArea: dataToUse.programmeArea || '',
    domain: dataToUse.domain || '',
    responsiblePerson: dataToUse.responsiblePerson || '',
    problemStatement: dataToUse.problemStatement || '',
    improvementFrom: dataToUse.improvementFrom || '',
    improvementTo: dataToUse.improvementTo || '',
    firstCycleDate: validateAndFormatDate(dataToUse.firstCycleDate) || dataToUse.firstCycleDate || '',
    projectEndDate: validateAndFormatDate(dataToUse.projectEndDate) || dataToUse.projectEndDate || '',

    // MISSING FIELDS - Add these important form fields
    projectCategory: dataToUse.projectCategory || '',
    department: dataToUse.department || '',
    subcategory: dataToUse.subcategory || '',
    initiativeSource: dataToUse.initiativeSource || '',
    measurementFrequency: 'MONTHLY', // Always set to MONTHLY as per requirement

    // Status field - critically important for API
    status: dataToUse.status || 'ongoing',

    // Ensure any custom KPI definition is included
    customKpiDefinition: dataToUse.customKpiDefinition || '',

    // KPI and Indicator fields - CRITICAL: Include numerator/denominator data
    useCustomIndicator: dataToUse.useCustomIndicator || false,
    selectedKPI: dataToUse.selectedKPI || '',
    indicatorDescription: dataToUse.indicatorDescription || '',
    indicatorNumerator: dataToUse.indicatorNumerator || '',
    indicatorDenominator: dataToUse.indicatorDenominator || '',
    numeratorNumber: dataToUse.numeratorNumber,
    denominatorNumber: dataToUse.denominatorNumber,

    // Include location fields from current user's jurisdiction data
    facility: currentUser?.facility || (dataToUse as any).facility || '',
    county: currentUser?.county || (dataToUse as any).county || '',
    subcounty: currentUser?.subCounty || (dataToUse as any).subcounty || '',

    // Initialize potential gaps
    potentialGaps: []
  };

  // Handle subDomain - submit "none" if not provided
  if (dataToUse.subDomain && dataToUse.subDomain.trim() !== '') {
    finalProjectData.subDomain = dataToUse.subDomain;
  } else {
    // Submit "none" if subdomain is not provided
    finalProjectData.subDomain = 'none';
  }

  // Handle potential gaps for Community Health Services
  if (dataToUse.programmeArea === 'Community Health Services' && dataToUse.potentialGaps) {
    finalProjectData.potentialGaps = dataToUse.potentialGaps.filter(gap => gap && gap.trim() !== '');
  }

  // Handle CHS team members - ENSURE ALL FIELDS ARE INCLUDED
  if (dataToUse.chsTeamMembers && dataToUse.chsTeamMembers.length > 0) {
    finalProjectData.chsTeamMembers = dataToUse.chsTeamMembers
      .filter(member => member.fullName && member.role)
      .map(member => ({
        fullName: member.fullName,
        phoneNumber: member.phoneNumber || '', // Explicitly include even if empty
        role: member.role,
        customRole: member.customRole || '' // Explicitly include even if empty
      }));
    
    if (finalProjectData.chsTeamMembers.length > 0) {
      }
  }

  // Handle participants
  if (dataToUse.participants && dataToUse.participants.length > 0) {
    finalProjectData.participants = dataToUse.participants.filter(participant => participant.fullName);
  }

  // Handle identified problems
  if (dataToUse.identifiedProblems && dataToUse.identifiedProblems.length > 0) {
    finalProjectData.identifiedProblems = dataToUse.identifiedProblems.filter(problem => problem && problem.trim() !== '');
  }

  // Handle decision matrix - map from problemRankings to decisionMatrix format
  if (dataToUse.problemRankings && dataToUse.problemRankings.length > 0) {
    // Include both problemRankings (original format) and decisionMatrix (mapped format)
    finalProjectData.problemRankings = dataToUse.problemRankings;
    const mappedDecisionMatrix = dataToUse.problemRankings.map((item: any) => ({
      gap: item.text,
      urgency: item.urgency || 0,
      cost: item.cost || 0,
      impact: item.impact || 0,
      feasibility: item.feasibility || 0,
      total: item.total || 0,
      rank: item.rank || 0
    }));
    
    // Include both field names for backend compatibility
    finalProjectData.decisionMatrix = mappedDecisionMatrix;
    finalProjectData.decisionMatrixItems = mappedDecisionMatrix; // Backend might expect this field name
  } else if (dataToUse.decisionMatrix && dataToUse.decisionMatrix.length > 0) {
    // Fallback to direct decisionMatrix if available
    finalProjectData.decisionMatrix = dataToUse.decisionMatrix;
    finalProjectData.decisionMatrixItems = dataToUse.decisionMatrix; // Backend compatibility
  }

  // Handle root cause matrix
  if (dataToUse.rootCauseMatrix && dataToUse.rootCauseMatrix.length > 0) {
    finalProjectData.rootCauseMatrix = dataToUse.rootCauseMatrix;
  }

  // Handle action plan - ENSURE ALL FIELDS ARE INCLUDED
  if (dataToUse.actionPlan && dataToUse.actionPlan.length > 0) {
    finalProjectData.actionPlan = dataToUse.actionPlan.map(item => ({
      action: item.action || '',
      responsiblePerson: item.responsiblePerson || '', // Explicitly include
      resources: item.resources || '',
      byWhen: item.byWhen || '', // Explicitly include date
      place: item.place || '', // Explicitly include
      mov: item.mov || '', // Explicitly include means of verification
      rootCause: item.rootCause || '', // Explicitly include root cause
      status: item.status || '' // Explicitly include status
    }));
    
    if (finalProjectData.actionPlan.length > 0) {
      }
  }

  // Handle fishbone analysis
  // IMPORTANT: Always include fishbone structure in submission, even if the form has no fishBoneAnalysis yet.
  // This ensures the backend consistently receives people/process/policy/place/provision arrays
  // and avoids missing fields in the DB. When fishBoneAnalysis is absent, the transformer
  // returns empty arrays which is acceptable and desired for a consistent schema.
  try {
    const transformedFishbone = transformFishboneDataForBackend(dataToUse);
    Object.assign(finalProjectData, transformedFishbone);
    
    // ALSO include the original fishBoneAnalysis structure for backend compatibility
    if (dataToUse.fishBoneAnalysis) {
      finalProjectData.fishBoneAnalysis = dataToUse.fishBoneAnalysis;
    }
    
    // DEBUG: Log what fishbone data is being included
    } catch (fishboneError) {
    console.error('Error transforming fishbone data:', fishboneError);
    // Continue without fishbone data rather than failing completely
  }

  // Handle KPI fields - ensure all KPI data is included
  finalProjectData.useCustomIndicator = dataToUse.useCustomIndicator || false;
  
  if (dataToUse.useCustomIndicator) {
    finalProjectData.indicatorNumerator = dataToUse.indicatorNumerator || '';
    finalProjectData.indicatorDenominator = dataToUse.indicatorDenominator || '';
    finalProjectData.indicatorDescription = `${dataToUse.indicatorNumerator || ''} / ${dataToUse.indicatorDenominator || ''}`;
  } else if (dataToUse.selectedKPI) {
    finalProjectData.selectedKPI = dataToUse.selectedKPI;
    finalProjectData.indicatorDescription = dataToUse.selectedKPI;
  }
  
  // Always include custom KPI definition if provided
  if (dataToUse.customKpiDefinition) {
    finalProjectData.customKpiDefinition = dataToUse.customKpiDefinition;
  }

  // Add numerical values for numerator and denominator if provided
  if (dataToUse.numeratorNumber !== undefined && dataToUse.numeratorNumber !== null) {
    finalProjectData.numeratorNumber = Number(dataToUse.numeratorNumber);
  }
  if (dataToUse.denominatorNumber !== undefined && dataToUse.denominatorNumber !== null) {
    finalProjectData.denominatorNumber = Number(dataToUse.denominatorNumber);
  }

  // Ensure tool selection fields have valid values
  finalProjectData.problemIdentificationTool = dataToUse.problemIdentificationTool || 'decisionMatrix';
  finalProjectData.rootCauseAnalysisTool = dataToUse.rootCauseAnalysisTool || 'fishbone';

  // Ensure required date fields are present
  if (!finalProjectData.createdAt) finalProjectData.createdAt = new Date().toISOString();
  if (!finalProjectData.updatedAt) finalProjectData.updatedAt = new Date().toISOString();

  // Add user information
  if (currentUser) {
    finalProjectData.createdBy = currentUser.email || currentUser.name || 'Unknown User';
  }

  return finalProjectData;
};

/**
 * Calculate the next due date for progress update based on measurement frequency
 */
export const calculateNextDueDate = (
  lastUpdateDate: string | Date, 
  measurementFrequency: string,
  projectStartDate?: string | Date
): Date => {
  // Validate input date
  const baseDate = lastUpdateDate ? new Date(lastUpdateDate) : new Date(projectStartDate || new Date());
  
  // Check if the base date is valid
  if (isNaN(baseDate.getTime())) {
    console.error('DEBUG - Invalid base date in calculateNextDueDate:', lastUpdateDate, projectStartDate);
    throw new Error('Invalid date provided to calculateNextDueDate');
  }
  
  const nextDue = new Date(baseDate);
  
  switch (measurementFrequency.toUpperCase()) {
    case 'DAILY':
      nextDue.setDate(nextDue.getDate() + 1);
      break;
    case 'WEEKLY':
      nextDue.setDate(nextDue.getDate() + 7);
      break;
    case 'BIWEEKLY':
      nextDue.setDate(nextDue.getDate() + 14);
      break;
    case 'MONTHLY':
      nextDue.setMonth(nextDue.getMonth() + 1);
      break;
    case 'QUARTERLY':
      nextDue.setMonth(nextDue.getMonth() + 3);
      break;
    default:
      // Default to monthly if frequency is not recognized
      nextDue.setMonth(nextDue.getMonth() + 1);
      break;
  }

  // Validate the result
  if (isNaN(nextDue.getTime())) {
    console.error('DEBUG - Invalid result date in calculateNextDueDate:', nextDue);
    throw new Error('Failed to calculate valid due date');
  }

  return nextDue;
};

/**
 * Check if a project is due for progress update within the specified number of days
 */
export const isProjectDueForUpdate = (
  project: any,
  daysAhead: number = 3
): boolean => {
  const now = new Date();
  const dueDate = getProjectDueDate(project);
  
  if (!dueDate) return false;
  
  const timeDiff = dueDate.getTime() - now.getTime();
  const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
  
  // Return true if due date is within the specified days (including overdue)
  return daysDiff <= daysAhead;
};

/**
 * Get the due date for a project's next progress update
 * Uses firstCycleDate as reference - returns the earliest missing cycle
 */
export const getProjectDueDate = (project: any): Date | null => {
  if (!project.measurementFrequency) {
    return null;
  }
  
  // Use the new logic to get the next required update
  const nextRequired = getNextRequiredUpdate(project);
  if (nextRequired) {
    return nextRequired;
  }
  
  // If no missed updates, calculate the next cycle from project start
  // Use firstCycleDate as the definitive project start date
  const projectStartDate = project.firstCycleDate || project.startDate || project.createdAt;
  if (!projectStartDate) {
    return null;
  }
  
  // Check for the problematic decimal date format
  if (typeof projectStartDate === 'string' && /^\d*\.?\d+$/.test(projectStartDate) && parseFloat(projectStartDate) < 1) {
    console.error('DEBUG - DECIMAL DATE DETECTED in project:', project.id, 'value:', projectStartDate);
    return null;
  }
  
  const startDate = new Date(projectStartDate);
  if (isNaN(startDate.getTime())) {
    return null;
  }
  
  const now = new Date();
  
  // If project hasn't started yet, the first cycle is 1 month after start
  if (startDate > now) {
    const firstCycleDate = new Date(startDate);
    firstCycleDate.setMonth(firstCycleDate.getMonth() + 1);
    return firstCycleDate;
  }
  
  // Calculate how many cycles should have passed by now
  const monthsSinceStart = Math.floor((now.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24 * 30.44)); // Average month length
  
  // Next cycle is one month after the current expected cycle count
  const nextCycleDate = new Date(startDate);
  nextCycleDate.setMonth(nextCycleDate.getMonth() + monthsSinceStart + 1);
  
  return nextCycleDate;
};

/**
 * Get days until due date (negative means overdue)
 */
export const getDaysUntilDue = (project: any): number | null => {
  const dueDate = getProjectDueDate(project);
  if (!dueDate) {
    return null;
  }
  
  // Validate the due date
  if (isNaN(dueDate.getTime())) {
    console.error('DEBUG - Invalid due date for project:', project.id, dueDate);
    return null;
  }
  
  const now = new Date();
  const timeDiff = dueDate.getTime() - now.getTime();
  const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
  
  return daysDiff;
};

/**
 * Calculate all missing monthly progress updates for a project
 * Uses firstCycleDate as reference - cycles are 1 month, 2 months, 3 months after project start
 * Returns array of due dates that haven't been completed yet
 */
export const getMissedProgressUpdates = (project: any): Date[] => {
  if (!project.measurementFrequency || project.measurementFrequency.toUpperCase() !== 'MONTHLY') {
    return [];
  }

  const missedUpdates: Date[] = [];
  const now = new Date();
  
  // Use firstCycleDate as the definitive project start date
  // This is the true project start date and should be the primary reference
  let projectStartDate;
  
  if (project.firstCycleDate) {
    projectStartDate = project.firstCycleDate;
    } else if (project.startDate) {
    projectStartDate = project.startDate;
    } else {
    projectStartDate = project.createdAt;
    }
  
  if (!projectStartDate) {
    return [];
  }
  
  const startDate = new Date(projectStartDate);
  if (isNaN(startDate.getTime())) {
    return [];
  }
  
  // If project hasn't started yet, there are no missed updates
  if (startDate > now) {
    return [];
  }
  
  // Get all completed test cycles to check which months are already done
  // Use FIFO resolution: assign cycles to earliest missing dates in order
  const completedCycles = new Set<string>();
  
  if (project.testCycles && project.testCycles.length > 0) {
    // Sort test cycles by submission date (createdAt) to maintain FIFO order
    const sortedCycles = [...project.testCycles].sort((a, b) => {
      const dateA = new Date(a.createdAt || a.endDate || a.startDate);
      const dateB = new Date(b.createdAt || b.endDate || b.startDate);
      return dateA.getTime() - dateB.getTime();
    });
    
    // Calculate all expected cycle dates from project start
    const expectedCycles: Date[] = [];
    let cycleNum = 1;
    let expectedDate = new Date(startDate);
    expectedDate.setMonth(expectedDate.getMonth() + cycleNum);
    
    while (expectedDate <= now) {
      expectedCycles.push(new Date(expectedDate));
      cycleNum++;
      expectedDate = new Date(startDate);
      expectedDate.setMonth(expectedDate.getMonth() + cycleNum);
    }
    
    // Assign each submitted update to the earliest available cycle (FIFO)
    sortedCycles.forEach((cycle, index) => {
      if (cycle.cycleResolutionDate) {
        // Use explicit resolution date if available
        const resolutionDate = new Date(cycle.cycleResolutionDate);
        const monthKey = resolutionDate.toISOString().substring(0, 7);
        completedCycles.add(monthKey);
        } else if (index < expectedCycles.length) {
        // Auto-assign to earliest available cycle (FIFO)
        const assignedCycle = expectedCycles[index];
        const monthKey = assignedCycle.toISOString().substring(0, 7);
        completedCycles.add(monthKey);
        }
    });
  }
  
  // Calculate all monthly cycles from project start date
  // 1st cycle = 1 month after start, 2nd cycle = 2 months after start, etc.
  let cycleNumber = 1;
  let cycleDate = new Date(startDate);
  cycleDate.setMonth(cycleDate.getMonth() + cycleNumber);
  
  while (cycleDate <= now) {
    const monthKey = cycleDate.toISOString().substring(0, 7);
    const isCompleted = completedCycles.has(monthKey);
    
    // If this cycle hasn't been completed, it's a missed update
    if (!isCompleted) {
      missedUpdates.push(new Date(cycleDate));
      }
    
    // Move to next cycle
    cycleNumber++;
    cycleDate = new Date(startDate);
    cycleDate.setMonth(cycleDate.getMonth() + cycleNumber);
  }
  
  return missedUpdates;
};

/**
 * Debug function to show project cycle schedule
 */
export const debugProjectCycleSchedule = (project: any): void => {
  // Show which date is being used
  let usedDate;
  if (project.firstCycleDate) {
    usedDate = project.firstCycleDate;
    } else if (project.startDate) {
    usedDate = project.startDate;
    } else {
    usedDate = project.createdAt;
    }
  
  if (usedDate) {
    const refDate = new Date(usedDate);
    }
  const missedUpdates = getMissedProgressUpdates(project);
  const nextRequired = getNextRequiredUpdate(project);
  const hasMultiple = hasSequentialUpdatesRequired(project);
  
  };

/**
 * Get the next required progress update date (earliest missing one)
 * Returns null if no updates are due
 */
export const getNextRequiredUpdate = (project: any): Date | null => {
  const missedUpdates = getMissedProgressUpdates(project);
  
  if (missedUpdates.length === 0) {
    return null;
  }
  
  // Return the earliest missed update (first in chronological order)
  const nextRequired = missedUpdates.sort((a, b) => a.getTime() - b.getTime())[0];
  return nextRequired;
};

/**
 * Check if a project has multiple missed updates that need sequential completion
 */
export const hasSequentialUpdatesRequired = (project: any): boolean => {
  const missedUpdates = getMissedProgressUpdates(project);
  return missedUpdates.length > 1;
};

/**
 * Get formatted status for project due date
 */
export const getProjectDueStatus = (project: any): { status: string; color: string; daysUntil: number | null } => {
  const daysUntil = getDaysUntilDue(project);
  
  if (daysUntil === null) {
    return { status: 'Unknown', color: 'grey', daysUntil: null };
  }
  
  if (daysUntil < 0) {
    return { status: `Overdue by ${Math.abs(daysUntil)} days`, color: 'error', daysUntil };
  } else if (daysUntil === 0) {
    return { status: 'Due Today', color: 'warning', daysUntil };
  } else if (daysUntil <= 3) {
    return { status: `Due in ${daysUntil} days`, color: 'warning', daysUntil };
  } else {
    return { status: `Due in ${daysUntil} days`, color: 'success', daysUntil };
  }
};

/**
 * CQI Project Status Types
 */
export type CQIProjectStatus = 
  | 'on_course'     // 🟢 On Course - Main category (includes subsets: up_to_date, due_for_update, due_today)
  | 'due_for_update' // 🟠 Due for Update in X days - Subset of On Course (7 days before due date)
  | 'up_to_date'    // 🟢 Up to Date - Subset of On Course (between day after update and 7 days before next)
  | 'due_today'     // 🔴 Progress Update Due Today - Subset of On Course (on the due date)
  | 'past_due'      // 🟠 Past Progress Update - 1-90 days overdue (NOT on course)
  | 'stalled'       // 🔴 Stalled - 91-180 days overdue (NOT on course)
  | 'abandoned';    // ⚫ Abandoned - 180+ days overdue (NOT on course)

/**
 * Get CQI Project Status based on progress update timeline
 */
export const getCQIProjectStatus = (project: any): {
  status: CQIProjectStatus;
  label: string;
  color: string;
  icon: string;
  description: string;
} => {
  // Only apply to ongoing projects
  if (!project || project.status !== 'ongoing') {
    return {
      status: 'on_course',
      label: 'On Course',
      color: '#4caf50', // Green
      icon: '🟢',
      description: 'Project is not in ongoing status'
    };
  }

  const daysUntilDue = getDaysUntilDue(project);
  
  if (daysUntilDue === null) {
    return {
      status: 'on_course',
      label: 'On Course',
      color: '#4caf50', // Green
      icon: '🟢',
      description: 'Unable to determine due date'
    };
  }

  // Calculate days past due (negative daysUntilDue means overdue)
  const daysPastDue = daysUntilDue < 0 ? Math.abs(daysUntilDue) : 0;
  
  // Check for consecutive missed updates for stalled status
  const missedUpdates = getMissedProgressUpdates(project);
  const consecutiveMissed = missedUpdates.length;

  // Status logic based on requirements
  if (daysPastDue >= 181) {
    // Day 181+ - Abandoned
    return {
      status: 'abandoned',
      label: 'Abandoned',
      color: '#424242', // Dark Grey
      icon: '⚫',
      description: 'Project has been inactive for over 180 days'
    };
  } else if (daysPastDue >= 91 || consecutiveMissed >= 3) {
    // Day 91-180 OR 3+ consecutive missed updates - Stalled
    return {
      status: 'stalled',
      label: 'Stalled',
      color: '#f44336', // Red
      icon: '🔴',
      description: `Project stalled - ${daysPastDue} days overdue or ${consecutiveMissed} missed updates`
    };
  } else if (daysPastDue >= 1) {
    // Day 1-90 past due - Past Progress Update
    return {
      status: 'past_due',
      label: `${daysPastDue} days Past Progress Update`,
      color: '#ff9800', // Orange
      icon: '🟠',
      description: `Progress update is ${daysPastDue} days overdue`
    };
  } else if (daysUntilDue === 0) {
    // Due today - Progress Update Due Today
    return {
      status: 'due_today',
      label: 'Progress Update Due Today',
      color: '#f44336', // Red
      icon: '🔴',
      description: 'Progress update is due today'
    };
  } else if (daysUntilDue <= 7 && daysUntilDue >= 1) {
    // 1-7 days before due - Due for Update in X days (countdown)
    return {
      status: 'due_for_update',
      label: `Due for Update in ${daysUntilDue} day${daysUntilDue > 1 ? 's' : ''}`,
      color: '#ff9800', // Orange
      icon: '🟠',
      description: `Progress update due in ${daysUntilDue} day${daysUntilDue > 1 ? 's' : ''}`
    };
  } else {
    // More than 7 days before due - Up to Date (between day after update and 7 days before next)
    return {
      status: 'up_to_date',
      label: 'Up to Date',
      color: '#4caf50', // Green
      icon: '🟢',
      description: 'Project is up to date with progress updates'
    };
  }
};

/**
 * Check if a CQI status is part of "On Course" category
 */
export const isOnCourseStatus = (status: CQIProjectStatus): boolean => {
  return ['on_course', 'up_to_date', 'due_for_update', 'due_today'].includes(status);
};

/**
 * Get summary of CQI project statuses for a list of projects
 */
export const getCQIStatusSummary = (projects: any[]): {
  [key in CQIProjectStatus]: {
    count: number;
    color: string;
    icon: string;
    label: string;
  }
} => {
  const summary = {
    on_course: { count: 0, color: '#4caf50', icon: '🟢', label: 'On Course' },
    up_to_date: { count: 0, color: '#4caf50', icon: '🟢', label: 'Up to Date' },
    due_for_update: { count: 0, color: '#ff9800', icon: '🟠', label: 'Due for Update' },
    due_today: { count: 0, color: '#f44336', icon: '🔴', label: 'Due Today' },
    past_due: { count: 0, color: '#ff9800', icon: '🟠', label: 'Past Due' },
    stalled: { count: 0, color: '#f44336', icon: '🔴', label: 'Stalled' },
    abandoned: { count: 0, color: '#424242', icon: '⚫', label: 'Abandoned' }
  };

  projects.forEach(project => {
    if (project.status === 'ongoing') {
      const cqiStatus = getCQIProjectStatus(project);
      summary[cqiStatus.status].count++;
    }
  });

  return summary;
};

/**
 * Check if a project has achieved its improvement target
 */
export const hasAchievedImprovementTarget = (project: any): boolean => {
  if (!project.improvementTo || !project.testCycles || !Array.isArray(project.testCycles)) {
    return false;
  }

  // Check if any test cycle has achieved the target
  return project.testCycles.some((cycle: any) => {
    if (!cycle.numerator || !cycle.denominator || cycle.denominator === 0) {
      return false;
    }
    
    const achievedPercentage = (cycle.numerator / cycle.denominator) * 100;
    const targetPercentage = project.improvementTo;
    
    return achievedPercentage >= targetPercentage;
  });
};

/**
 * Get the actual completion percentage from the latest test cycle
 */
export const getLatestCompletionPercentage = (project: any): number | null => {
  if (!project.testCycles || !Array.isArray(project.testCycles) || project.testCycles.length === 0) {
    return null;
  }

  // Get the most recent test cycle
  const latestCycle = project.testCycles[project.testCycles.length - 1];
  
  if (!latestCycle.numerator || !latestCycle.denominator || latestCycle.denominator === 0) {
    return null;
  }

  return (latestCycle.numerator / latestCycle.denominator) * 100;
};

/**
 * Check if a project should be automatically marked as completed
 */
export const shouldAutoCompleteProject = (project: any): boolean => {
  // Only ongoing projects can be auto-completed
  if (project.status !== 'ongoing') {
    return false;
  }

  return hasAchievedImprovementTarget(project);
};

/**
 * Get grouped CQI status summary with "On Course" as main category and subsets
 */
export const getGroupedCQIStatusSummary = (projects: any[]): {
  onCourse: {
    total: number;
    color: string;
    icon: string;
    label: string;
    subsets: {
      up_to_date: { count: number; label: string; color: string; icon: string; };
      due_for_update: { count: number; label: string; color: string; icon: string; };
      due_today: { count: number; label: string; color: string; icon: string; };
    };
  };
  offCourse: {
    past_due: { count: number; label: string; color: string; icon: string; };
    stalled: { count: number; label: string; color: string; icon: string; };
    abandoned: { count: number; label: string; color: string; icon: string; };
  };
} => {
  const detailedSummary = getCQIStatusSummary(projects);
  
  const onCourseTotal = detailedSummary.on_course.count + 
                       detailedSummary.up_to_date.count + 
                       detailedSummary.due_for_update.count + 
                       detailedSummary.due_today.count;

  return {
    onCourse: {
      total: onCourseTotal,
      color: '#4caf50', // Green
      icon: '🟢',
      label: 'On Course',
      subsets: {
        up_to_date: {
          count: detailedSummary.up_to_date.count,
          label: 'Up to Date',
          color: '#4caf50',
          icon: '🟢'
        },
        due_for_update: {
          count: detailedSummary.due_for_update.count,
          label: 'Due for Update',
          color: '#ff9800',
          icon: '🟠'
        },
        due_today: {
          count: detailedSummary.due_today.count,
          label: 'Due Today',
          color: '#f44336',
          icon: '🔴'
        }
      }
    },
    offCourse: {
      past_due: {
        count: detailedSummary.past_due.count,
        label: 'Past Due',
        color: '#ff9800',
        icon: '🟠'
      },
      stalled: {
        count: detailedSummary.stalled.count,
        label: 'Stalled',
        color: '#f44336',
        icon: '🔴'
      },
      abandoned: {
        count: detailedSummary.abandoned.count,
        label: 'Abandoned',
        color: '#424242',
        icon: '⚫'
      }
    }
  };
};
